/* ============================================================
 * a11y-alt-labels.js
 * Accessibility remediation — Converge audit 2026-05-22, findings #1, #6, #72.
 * Added 2026-07-29.
 *
 * THE PROBLEM
 * Product image alt text on this store doubles as a variant-image filter.
 * assets/gallery.js -> filterSlidesByOptions() reads the alt attribute off the
 * rendered DOM and matches #hashtags against the selected variant options:
 *
 *     const alt = media ? media.getAttribute("alt") : "";
 *     const altHashtags = (alt?.match(/#[^\s#]+/g) || [])...
 *
 * So alt values look like:
 *     "#pepper A close-up of the Cole & Mason Portobello mill..."
 *     "#7\"#salt&pepperset Two mills side by side..."
 *
 * A screen reader announces the whole string, tags included — "hash pepper A
 * close-up of...". That is finding #6.
 *
 * WHY NOT JUST CLEAN THE ALT TEXT
 * Removing the tags from the alt data, or stripping them in Liquid, would break
 * variant image filtering across 64 products. The tags MUST stay in alt.
 *
 * THE FIX
 * aria-label takes precedence over alt when computing an accessible name. So we
 * leave alt untouched for gallery.js and add an aria-label carrying only the
 * human-readable part. Screen readers read the aria-label; the filter still
 * reads alt. Nothing about merchandising changes — staff keep typing #tags.
 *
 * SCOPE, DELIBERATELY NARROW
 * Only images whose alt still has real descriptive text after the tags are
 * removed. Where alt is tag-only ("#pepper") there is no description to
 * promote, and inventing one — or reusing the product title across every image
 * — would recreate finding #72 (identical alt across a carousel). Those are
 * fixed in the data instead, by appending a description after the tag.
 *
 * The 192 bare "#" values are inert: gallery.js requires at least one character
 * after the hash, so they never match. They are also fixed in the data pass.
 * ============================================================ */

(function () {
  "use strict";

  // Must mirror the pattern in gallery.js exactly, or we would strip something
  // the filter still depends on.
  var TAG = /#[^\s#]+/g;
  var DONE = "data-a11y-label-applied";

  function cleanLabel(alt) {
    if (!alt || alt.indexOf("#") === -1) return null;

    var text = alt.replace(TAG, " ")   // drop #salt, #7", #salt&pepperset ...
                  .replace(/#/g, " ")  // and any leftover bare hash
                  .replace(/\s+/g, " ")
                  .trim();

    // Require actual words. Punctuation or an empty remainder means there was no
    // description to promote, so we leave this one to the data pass.
    if (!/[A-Za-z]{3}/.test(text)) return null;

    return text;
  }

  function apply(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var media = scope.querySelectorAll('img[alt*="#"], video[alt*="#"]');

    for (var i = 0; i < media.length; i++) {
      var el = media[i];
      if (el.hasAttribute(DONE)) continue;

      var label = cleanLabel(el.getAttribute("alt"));
      if (label) {
        el.setAttribute("aria-label", label);
        el.setAttribute(DONE, "");
      }
    }
  }

  function run() {
    try {
      apply(document);
    } catch (e) {
      // Never let this break the page. Worst case, alt text is announced with
      // its tags, which is the pre-existing behaviour.
      if (window.console && console.warn) {
        console.warn("a11y-alt-labels: ", e);
      }
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }

  // gallery.js clones and re-appends slides whenever the variant changes, which
  // discards our attributes. It dispatches this event after each rebuild, so we
  // reapply. Using the theme's own event rather than a MutationObserver keeps
  // this cheap and predictable.
  document.addEventListener("gallery:updated", function () {
    // Defer one tick: the event fires during slide decoration, before the new
    // nodes are necessarily in place.
    setTimeout(run, 0);
  });

  // Variant changes can also repaint media outside the gallery component.
  document.addEventListener("variant:change", run);
})();
